"use client";

import { useState, useCallback } from "react";
import { marked } from "marked";
import axios from "axios";
import {
  Box,
  Button,
  Paper,
  Typography,
  LinearProgress,
  Alert,
  IconButton,
  Stack,
  Fade,
  Zoom,
  TextField,
  Tabs,
  Tab,
} from "@mui/material";
import {
  CloudUpload,
  Delete,
  Code,
  CheckCircle,
  ContentCopy,
  Download,
  Visibility,
  EditNote,
  Close,
  InsertDriveFile,
} from "@mui/icons-material";

interface HtmlFile {
  file: File;
  id: string;
  content: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function HtmlToMarkdownConverter() {
  const [htmlFile, setHtmlFile] = useState<HtmlFile | null>(null);
  const [htmlText, setHtmlText] = useState<string>("");
  const [markdownOutput, setMarkdownOutput] = useState<string>("");
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [inputTab, setInputTab] = useState(0);
  const [outputTab, setOutputTab] = useState(0);
  const [copied, setCopied] = useState(false);

  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    const fileName = file.name.toLowerCase();
    const isValidExtension =
      fileName.endsWith(".html") ||
      fileName.endsWith(".htm") ||
      fileName.endsWith(".txt");

    if (
      isValidExtension ||
      file.type === "text/html" ||
      file.type === "text/plain"
    ) {
      const content = await file.text();
      setHtmlFile({
        file,
        id: Math.random().toString(36).substr(2, 9),
        content,
      });
      setHtmlText(content);
      setError(null);
      setSuccess(false);
      setMarkdownOutput("");
    } else {
      setError(
        `${file.name} is not a supported format. Please use .html, .htm, or .txt files.`
      );
    }
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFileSelect(e.dataTransfer.files);
      }
    },
    [handleFileSelect]
  );

  const removeFile = () => {
    setHtmlFile(null);
    setHtmlText("");
    setError(null);
    setSuccess(false);
    setMarkdownOutput("");
    const fileInput = document.getElementById(
      "html-file-input"
    ) as HTMLInputElement | null;
    if (fileInput) fileInput.value = "";
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const convertToMarkdown = async () => {
    const textToConvert = htmlText.trim();

    if (!textToConvert) {
      setError("Please enter some HTML text or upload a file to convert.");
      return;
    }

    setConverting(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await axios.post(
        "/api/html-to-markdown",
        { html: textToConvert },
        {
          timeout: 600000,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      setMarkdownOutput(response.data.markdown);
      setSuccess(true);
    } catch (err) {
      let errorMessage = "Failed to convert HTML to Markdown";
      if (axios.isAxiosError(err) && err.response) {
        const errorData = err.response.data;
        errorMessage = errorData.error || errorMessage;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setConverting(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(markdownOutput);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Failed to copy to clipboard");
    }
  };

  const downloadMarkdown = () => {
    const blob = new Blob([markdownOutput], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const fileName =
      htmlFile?.file.name.replace(/\.(html|htm|txt)$/i, "") || "converted";
    a.download = `${fileName}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleInputTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setInputTab(newValue);
  };

  const handleOutputTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setOutputTab(newValue);
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <Paper
        elevation={8}
        sx={{
          p: 4,
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(10px)",
        }}
      >
        {/* Input Method Tabs */}
        <Tabs
          value={inputTab}
          onChange={handleInputTabChange}
          sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab icon={<EditNote />} label="Type HTML" iconPosition="start" />
          <Tab
            icon={<CloudUpload />}
            label="Upload File"
            iconPosition="start"
          />
        </Tabs>

        {/* Type HTML Tab */}
        <TabPanel value={inputTab} index={0}>
          <TextField
            multiline
            rows={10}
            fullWidth
            placeholder='<h1>Enter your HTML here...</h1>

<h2>Example</h2>

<p>This is <strong>bold</strong> text and this is <em>italic</em>.</p>

<ul>
  <li>List item 1</li>
  <li>List item 2</li>
</ul>

<pre><code>const greeting = "Hello, World!";
console.log(greeting);
</code></pre>'
            value={htmlText}
            onChange={(e) => {
              setHtmlText(e.target.value);
              setSuccess(false);
              setMarkdownOutput("");
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontFamily: "monospace",
                fontSize: "0.9rem",
              },
            }}
          />
        </TabPanel>

        {/* Upload File Tab */}
        <TabPanel value={inputTab} index={1}>
          <Box
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            sx={{
              border: "3px dashed",
              borderColor: dragActive ? "primary.main" : "grey.300",
              borderRadius: 3,
              p: 4,
              textAlign: "center",
              bgcolor: dragActive ? "primary.50" : "grey.50",
              cursor: "pointer",
              transition: "all 0.3s ease",
              position: "relative",
              "&:hover": {
                borderColor: "primary.main",
                bgcolor: "primary.50",
              },
            }}
            onClick={() => document.getElementById("html-file-input")?.click()}
          >
            <input
              id="html-file-input"
              type="file"
              accept=".html,.htm,.txt,text/html,text/plain"
              onChange={(e) => handleFileSelect(e.target.files)}
              style={{ display: "none" }}
            />

            <CloudUpload
              sx={{
                fontSize: 80,
                color: dragActive ? "primary.main" : "grey.400",
                mb: 2,
              }}
            />

            <Typography
              variant="h6"
              component="p"
              gutterBottom
              fontWeight={600}
              color="text.primary"
            >
              {dragActive
                ? "Drop your HTML file here"
                : "Click to upload or drag and drop"}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Supported formats: .html, .htm, .txt
            </Typography>

            <Button
              variant="contained"
              startIcon={<InsertDriveFile sx={{ color: "#ffffff" }} />}
              sx={{
                mt: 3,
                color: "#ffffff",
                backgroundColor: "#5558d3",
                "&:hover": {
                  backgroundColor: "#4448c2",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                document.getElementById("html-file-input")?.click();
              }}
            >
              Select HTML File
            </Button>
          </Box>

          {/* Selected File Preview */}
          {htmlFile && (
            <Fade in={!!htmlFile}>
              <Box sx={{ mt: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" component="p" fontWeight={600}>
                    Selected File
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Delete />}
                    onClick={removeFile}
                    color="error"
                    variant="outlined"
                  >
                    Remove
                  </Button>
                </Box>

                <Zoom in>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 3,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      bgcolor: "grey.50",
                      borderRadius: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 2,
                        bgcolor: "#1976D2",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Code sx={{ fontSize: 32, color: "white" }} />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {htmlFile.file.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatFileSize(htmlFile.file.size)}
                      </Typography>
                    </Box>

                    <IconButton
                      onClick={removeFile}
                      sx={{
                        bgcolor: "rgba(255,255,255,0.9)",
                        "&:hover": {
                          bgcolor: "error.main",
                          color: "white",
                        },
                      }}
                    >
                      <Close />
                    </IconButton>
                  </Paper>
                </Zoom>
              </Box>
            </Fade>
          )}
        </TabPanel>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mt: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mt: 3 }}
            icon={<CheckCircle />}
            onClose={() => setSuccess(false)}
          >
            HTML converted to Markdown successfully!
          </Alert>
        )}

        {/* Convert Button */}
        <Box sx={{ mt: 4 }}>
          {converting && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<Code sx={{ color: "#ffffff" }} />}
            onClick={convertToMarkdown}
            disabled={converting || !htmlText.trim()}
            style={{
              color: "#ffffff",
            }}
            sx={{
              py: 2,
              fontSize: "1.1rem",
              fontWeight: 600,
              background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
              boxShadow: "0 3px 15px rgba(102, 126, 234, 0.3)",
              "&:hover": {
                background: "linear-gradient(45deg, #5568d3 30%, #63408b 90%)",
                boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
              },
              "&.Mui-disabled": {
                background: "#e0e0e0",
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            {converting ? "Converting..." : "Convert to Markdown"}
          </Button>
        </Box>

        {/* Markdown Output */}
        {markdownOutput && (
          <Fade in={!!markdownOutput}>
            <Box sx={{ mt: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" component="p" fontWeight={600}>
                  Markdown Output
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    startIcon={copied ? <CheckCircle /> : <ContentCopy />}
                    onClick={copyToClipboard}
                    variant="outlined"
                    color={copied ? "success" : "primary"}
                  >
                    {copied ? "Copied!" : "Copy"}
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Download />}
                    onClick={downloadMarkdown}
                    variant="contained"
                    sx={{
                      background:
                        "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
                    }}
                  >
                    Download
                  </Button>
                </Stack>
              </Box>

              <Tabs
                value={outputTab}
                onChange={handleOutputTabChange}
                sx={{ borderBottom: 1, borderColor: "divider" }}
              >
                <Tab
                  icon={<Code />}
                  label="Markdown Code"
                  iconPosition="start"
                />
                <Tab
                  icon={<Visibility />}
                  label="Preview"
                  iconPosition="start"
                />
              </Tabs>

              <TabPanel value={outputTab} index={0}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    mt: 2,
                    bgcolor: "#1e1e1e",
                    borderRadius: 2,
                    maxHeight: "400px",
                    overflow: "auto",
                  }}
                >
                  <Typography
                    component="pre"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.85rem",
                      color: "#d4d4d4",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      margin: 0,
                    }}
                  >
                    {markdownOutput}
                  </Typography>
                </Paper>
              </TabPanel>

              <TabPanel value={outputTab} index={1}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    mt: 2,
                    borderRadius: 2,
                    maxHeight: "400px",
                    overflow: "auto",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    component="div"
                    dangerouslySetInnerHTML={{
                      __html: marked(markdownOutput),
                    }}
                    sx={{
                      "& h1, & h2, & h3, & h4, & h5, & h6": {
                        mt: 2,
                        mb: 1,
                        fontWeight: 600,
                      },
                      "& h1": { fontSize: "2rem" },
                      "& h2": { fontSize: "1.5rem" },
                      "& h3": { fontSize: "1.25rem" },
                      "& p": { mb: 2 },
                      "& ul, & ol": { pl: 3, mb: 2 },
                      "& code": {
                        bgcolor: "grey.100",
                        px: 0.5,
                        py: 0.25,
                        borderRadius: 1,
                        fontFamily: "monospace",
                        fontSize: "0.9em",
                      },
                      "& pre": {
                        bgcolor: "grey.100",
                        p: 2,
                        borderRadius: 1,
                        overflow: "auto",
                        "& code": {
                          bgcolor: "transparent",
                          p: 0,
                        },
                      },
                      "& blockquote": {
                        borderLeft: "4px solid",
                        borderColor: "primary.main",
                        pl: 2,
                        ml: 0,
                        fontStyle: "italic",
                        color: "text.secondary",
                      },
                      "& table": {
                        width: "100%",
                        borderCollapse: "collapse",
                        mb: 2,
                      },
                      "& th, & td": {
                        border: "1px solid",
                        borderColor: "divider",
                        p: 1,
                      },
                      "& th": {
                        bgcolor: "grey.100",
                        fontWeight: 600,
                      },
                      "& a": {
                        color: "primary.main",
                      },
                      "& img": {
                        maxWidth: "100%",
                        height: "auto",
                      },
                    }}
                  />
                </Paper>
              </TabPanel>
            </Box>
          </Fade>
        )}
      </Paper>
    </Box>
  );
}
